// src/interfaces/webCitationTypes.ts

/**
 * Inline web citations rebuilt by the backend from Gemini's Google Search
 * grounding.
 *
 * Claude writes its web citations into the answer as <cite source url/> tags.
 * Gemini only reports which parts of an answer web search supports once the
 * answer has finished streaming, so for Gemini the backend sends a
 * "web_citations" chunk saying where to splice those same tags into the
 * finished message instead.
 */

export interface WebCitationSource {
  /** Label for the chip: the source's site */
  title: string;
  /** Link to the source as Google provides it */
  url: string;
  /** The page's own title, when Google gives one (pages read with URL context) */
  page_title?: string;
}

export interface WebCitationInsertion {
  /** Position in the whole response text, in UTF-16 code units (a JS string index) */
  at: number;
  /**
   * The response text just before `at`, as the backend saw it. The tags are only
   * spliced in when the message text matches, so a mismatch drops the citation
   * rather than putting it in the wrong place.
   */
  anchor: string;
  /** One chip per source, in order */
  sources: WebCitationSource[];
}

const isWebCitationSource = (value: unknown): value is WebCitationSource => {
  const source = value as WebCitationSource;

  return (
    typeof source === "object" &&
    source !== null &&
    typeof source.title === "string" &&
    typeof source.url === "string" &&
    source.url.length > 0 &&
    (source.page_title === undefined || typeof source.page_title === "string")
  );
};

const isWebCitationInsertion = (value: unknown): value is WebCitationInsertion => {
  const insertion = value as WebCitationInsertion;

  return (
    typeof insertion === "object" &&
    insertion !== null &&
    Number.isInteger(insertion.at) &&
    insertion.at > 0 &&
    typeof insertion.anchor === "string" &&
    // An empty anchor would match anywhere and skip the splice check.
    insertion.anchor.length > 0 &&
    Array.isArray(insertion.sources)
  );
};

/**
 * The usable insertions from a "web_citations" chunk payload. Malformed
 * entries and sources are dropped individually; anything else yields [].
 */
export const normalizeWebCitations = (value: unknown): WebCitationInsertion[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(isWebCitationInsertion).flatMap((insertion) => {
    const sources = insertion.sources.filter(isWebCitationSource);

    return sources.length > 0 ? [{ at: insertion.at, anchor: insertion.anchor, sources }] : [];
  });
};
